import React, { useState } from "react";
import PurchaseOrderTab from "./tabs/PurchaseOrderTab";
import BuyerAddressTab from "./tabs/BuyerAddressTab";
import MiscTab from "./tabs/MiscTab";
import UserDefinedTab from "./tabs/UserDefinedTab";
import DetailTabs from "./DetailTabs";
import { 
  purchaseOrderFields, 
  supplierAddressFields, 
  buyerAddressFields, 
  miscFields, 
  userDefinedFields 
} from "./formFields.js/PurchaseOrderFields";
import { toast } from '../../components/AppToast';

export default function CreatePo({ initialValues = {}, onCancel, onSubmit, mode = 'create', readOnly = false }) {
  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState(initialValues);
  
  console.log("CreatePo formData:", formData);
  console.log("CreatePo  Initial values:", initialValues);
  // Add state to track if form submission was attempted
  const [formSubmissionAttempted, setFormSubmissionAttempted] = useState(false);

  const tabs = [
    { label: "PO Header", component: PurchaseOrderTab },
    { label: "Address", component: BuyerAddressTab },
    { label: "Misc", component: MiscTab },
    { label: "User Defined", component: UserDefinedTab },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Add validation function
  const validateForm = () => {
    const allFieldGroups = [
      { fields: purchaseOrderFields, tabIndex: 0, tabName: "PO Header" },
      { fields: [...supplierAddressFields, ...buyerAddressFields], tabIndex: 1, tabName: "Address" },
      { fields: miscFields, tabIndex: 2, tabName: "Misc" },
      { fields: userDefinedFields, tabIndex: 3, tabName: "User Defined" },
    ];

    const errors = [];

    for (const group of allFieldGroups) {
      for (const field of group.fields) {
        // Skip validation for fields that are hidden or read-only
        if (field.hiddenOnCreate || field.readOnly || !field.required) {
          continue;
        }

        // Skip conditional fields that shouldn't be visible
        if (field.conditional) {
          const shouldShow = formData[field.conditional.field] === field.conditional.value;
          if (!shouldShow) continue;
        }

        const value = formData[field.name];
        const isEmpty = value === null || value === undefined || value === '' || 
                       (typeof value === 'string' && value.trim() === '');

        if (isEmpty) {
          errors.push({
            fieldName: field.name,
            fieldLabel: field.label,
            tabIndex: group.tabIndex,
            tabName: group.tabName
          });
        }
      }
    }

    return errors;
  };

  // Function to focus on a specific field
  const focusOnField = (fieldName) => {
    setTimeout(() => {
      const fieldElement = document.getElementById(fieldName) || 
                          document.querySelector(`[name="${fieldName}"]`);
      if (fieldElement) {
        fieldElement.focus();
        fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        
        // Add visual highlight
        fieldElement.style.boxShadow = '0 0 0 3px rgba(220, 53, 69, 0.25)';
        fieldElement.style.borderColor = '#dc3545';
        
        // Remove highlight after 3 seconds
        setTimeout(() => {
          fieldElement.style.boxShadow = '';
          fieldElement.style.borderColor = '';
        }, 3000);
      }
    }, 100);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (readOnly) return;

    // Set form submission attempted to true to trigger validation display
    setFormSubmissionAttempted(true);

    // Validate all required fields
    const validationErrors = validateForm();
    
    if (validationErrors.length > 0) {
      const firstError = validationErrors[0];
      
      // Show error message with details
      const errorFieldsList = validationErrors.slice(0, 3).map(err => err.fieldLabel).join(', ');
      const moreErrors = validationErrors.length > 3 ? ` and ${validationErrors.length - 3} more` : '';
      
      toast.error(
        `Please fill in all required fields: ${errorFieldsList}${moreErrors}`,
        {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        }
      );

      // Switch to the tab containing the first invalid field
      if (firstError.tabIndex !== activeTab) {
        console.log(`Switching to tab: ${firstError.tabName} (index: ${firstError.tabIndex})`);
        setActiveTab(firstError.tabIndex);
      }

      // Focus on the first invalid field
      focusOnField(firstError.fieldName);
      
      return; // Prevent form submission
    }

    // If validation passes, proceed with submission
    if (onSubmit) onSubmit(formData);
  };

  // Add validation indicators to tab labels - only show if submission was attempted
  const getTabValidationStatus = (tabIndex) => {
    // Don't show validation indicators until form submission is attempted
    if (!formSubmissionAttempted) return false;
    
    const fieldGroups = [
      purchaseOrderFields,
      [...supplierAddressFields, ...buyerAddressFields],
      miscFields,
      userDefinedFields,
    ];

    const fields = fieldGroups[tabIndex] || [];
    const hasRequiredFields = fields.some(field => {
      if (!field.required || field.hiddenOnCreate || field.readOnly) return false;
      
      if (field.conditional) {
        const shouldShow = formData[field.conditional.field] === field.conditional.value;
        if (!shouldShow) return false;
      }
      
      const value = formData[field.name];
      const isEmpty = value === null || value === undefined || value === '' || 
                     (typeof value === 'string' && value.trim() === '');
      return isEmpty;
    });

    return hasRequiredFields;
  };

  const ActiveComponent = tabs[activeTab].component;

  const handleDetailUpdate = (detailData) => {
    // merge detailItems into formData but DO NOT trigger final submit
    const updatedFormData = {
      ...formData,
      ...detailData
    };
    setFormData(updatedFormData);
    // do not call onSubmit here
  };

  return (
    <>
      {/* Tabs Navigation with validation indicators */}
      <ul className="nav nav-tabs mb-3">
        {tabs.map((tab, index) => {
          const hasErrors = getTabValidationStatus(index);
          return (
            <li className="nav-item" key={index}>
              <button
                className={`nav-link ${activeTab === index ? "active" : ""} ${hasErrors ? "text-danger" : ""}`}
                onClick={() => setActiveTab(index)}
                type="button"
                style={{
                  position: 'relative',
                  ...(hasErrors ? { 
                    borderColor: '#dc3545',
                    backgroundColor: activeTab === index ? '#f8d7da' : 'transparent'
                  } : {})
                }}
              >
                {tab.label}
                {hasErrors && (
                  <span 
                    className="badge bg-danger rounded-pill ms-1" 
                    style={{ fontSize: '0.6em' }}
                    title="This tab has required fields that need to be filled"
                  >
                    !
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>

      {/* Active Tab Content - Pass formSubmissionAttempted to child components */}
      <ActiveComponent 
        formData={formData} 
        onChange={handleChange} 
        readOnly={readOnly}
        formSubmissionAttempted={formSubmissionAttempted}
        mode={mode} 
      />

      {/* Detail Section */}
      <hr />
      {/* <h4 className="text-center my-3">{mode === 'edit' || !readOnly ?'Add Items1':'View Items'}</h4> */}
      <DetailTabs 
        initialValues={formData} 
        onDetailsChange={handleDetailUpdate} 
        readOnly={readOnly}
      />

      {/* Footer */}
      <div className="d-flex justify-content-center gap-2 p-3 mt-5 ">
        <button className="btn btn-secondary" onClick={onCancel}>Back</button>
        {!readOnly && (
          <button className="btn btn-primary" onClick={handleSubmit}>
            {mode === 'edit' ? 'Update PO' : 'Create PO'}
          </button>
        )}
      </div>
    </>
  );
}